/**
 * useCrudOperations Hook
 *
 * Reusable custom hook for CRUD operations in management components.
 * Handles loading states, error handling, and success messages.
 *
 * @example
 * const { data, loading, error, successMessage, create, update, remove, loadData } = useCrudOperations({
 *   apiService: companiesAPI,
 *   resourceName: 'company'
 * });
 */

import { useState, useCallback } from 'react';

export function useCrudOperations({ apiService, resourceName = 'item', initialData = [] }) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  /**
   * Clear messages after a timeout
   */
  const clearMessage = useCallback((type, timeout = 3000) => {
    setTimeout(() => {
      if (type === 'error') setError('');
      if (type === 'success') setSuccessMessage('');
    }, timeout);
  }, []);

  /**
   * Load all data
   */
  const loadData = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getAll(params);

      if (response.success) {
        setData(response.data);
        return response.data;
      } else {
        throw new Error(response.error || `Failed to load ${resourceName}s`);
      }
    } catch (err) {
      console.error(`Error loading ${resourceName}s:`, err);
      const errorMsg = err.response?.data?.error || err.message || `Failed to load ${resourceName}s`;
      setError(errorMsg);
      clearMessage('error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiService, resourceName, clearMessage]);

  /**
   * Get single item by ID
   */
  const getById = useCallback(async (id) => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.getById(id);

      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error || `Failed to load ${resourceName}`);
      }
    } catch (err) {
      console.error(`Error loading ${resourceName}:`, err);
      const errorMsg = err.response?.data?.error || err.message || `Failed to load ${resourceName}`;
      setError(errorMsg);
      clearMessage('error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiService, resourceName, clearMessage]);

  /**
   * Create new item
   */
  const create = useCallback(async (itemData, successMsg = null) => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.create(itemData);

      if (response.success) {
        const message = successMsg || `${resourceName} created successfully`;
        setSuccessMessage(message);
        clearMessage('success');

        // Add to local data
        setData(prev => [...prev, response.data]);

        return response.data;
      } else {
        throw new Error(response.error || `Failed to create ${resourceName}`);
      }
    } catch (err) {
      console.error(`Error creating ${resourceName}:`, err);
      const errorMsg = err.response?.data?.error || err.message || `Failed to create ${resourceName}`;
      setError(errorMsg);
      clearMessage('error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiService, resourceName, clearMessage]);

  /**
   * Update existing item
   */
  const update = useCallback(async (id, updates, successMsg = null) => {
    try {
      setLoading(true);
      setError('');

      const response = await apiService.update(id, updates);

      if (response.success) {
        const message = successMsg || `${resourceName} updated successfully`;
        setSuccessMessage(message);
        clearMessage('success');

        // Update local data
        setData(prev => prev.map(item => item.id === id ? response.data : item));

        return response.data;
      } else {
        throw new Error(response.error || `Failed to update ${resourceName}`);
      }
    } catch (err) {
      console.error(`Error updating ${resourceName}:`, err);
      const errorMsg = err.response?.data?.error || err.message || `Failed to update ${resourceName}`;
      setError(errorMsg);
      clearMessage('error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiService, resourceName, clearMessage]);

  /**
   * Delete item
   */
  const remove = useCallback(async (id, confirmMsg = null, successMsg = null) => {
    const confirmed = confirmMsg ? window.confirm(confirmMsg) : true;

    if (!confirmed) return false;

    try {
      setLoading(true);
      setError('');

      const response = await apiService.delete(id);

      if (response.success) {
        const message = successMsg || `${resourceName} deleted successfully`;
        setSuccessMessage(message);
        clearMessage('success');

        // Remove from local data
        setData(prev => prev.filter(item => item.id !== id));

        return true;
      } else {
        throw new Error(response.error || `Failed to delete ${resourceName}`);
      }
    } catch (err) {
      console.error(`Error deleting ${resourceName}:`, err);
      const errorMsg = err.response?.data?.error || err.message || `Failed to delete ${resourceName}`;
      setError(errorMsg);
      clearMessage('error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiService, resourceName, clearMessage]);

  /**
   * Set data directly (useful for external updates)
   */
  const setDataDirectly = useCallback((newData) => {
    setData(newData);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError('');
  }, []);

  /**
   * Clear success message
   */
  const clearSuccess = useCallback(() => {
    setSuccessMessage('');
  }, []);

  return {
    // State
    data,
    loading,
    error,
    successMessage,

    // Methods
    loadData,
    getById,
    create,
    update,
    remove,
    setData: setDataDirectly,
    clearError,
    clearSuccess
  };
}

export default useCrudOperations;
